import { TextField, Button, Icon, IconSize, Slider } from '@sbt-web/ui';
import { formatDistance } from '@sbt-web/utils';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import itemClasses from '../common/Item.module.scss.js';
import RecentSearches from '../common/RecentSearches.js';
import SuggestionResult from '../common/SuggestionResult.js';
import { buildSearchSubmitEvent, buildSearchCancelEvent, buildRadiusSearchCancelEvent, buildRadiusSearchInitiateEvent } from '../common/tracking.js';
import { isGeogosAutocompleteOption } from '../common/types.js';
import { mapper } from '../useGeogos.js';
import HereMap from './HereMap.js';
import Map from './assets/map.js';
import classes from './index.module.scss.js';

const textFieldRef = React.createRef();
var Page;
(function (Page) {
    Page[Page["ModeSelection"] = 0] = "ModeSelection";
    Page[Page["RadiusMap"] = 1] = "RadiusMap";
    Page[Page["Geogos"] = 2] = "Geogos";
})(Page || (Page = {}));
const defaultRadius = 1000;
function MobileGeoSearch({ assetsBase, value, options, labelId, name, onSelect, pulse, updateItems, storeRecentSearch, resultsPreview, updateResultsPreview, variant, hereApiKey, }) {
    const [isOpen, setIsOpen] = useState(false);
    const [previousInput, setPreviousInput] = useState('');
    const [page, setPage] = useState(value.responseFrom === 'geogos' ? Page.Geogos : Page.RadiusMap);
    const [selectedPoint, setSelectedPoint] = useState(() => {
        if (value.responseFrom === 'here') {
            return {
                friendlyName: value.entry.friendlyName,
                lat: value.entry.point.lat,
                lng: value.entry.point.lng,
            };
        }
        else {
            return undefined;
        }
    });
    const [radius, setRadius] = useState(() => {
        if (value.responseFrom === 'here') {
            return value.radius;
        }
        else {
            return defaultRadius;
        }
    });
    const [previousSelection, setPreviousSelection] = useState(value);
    useEffect(() => {
        if (isOpen && selectedPoint != undefined && page === Page.RadiusMap) {
            updateResultsPreview?.({
                responseFrom: 'here',
                entry: {
                    friendlyName: selectedPoint.friendlyName,
                    point: { lat: selectedPoint.lat, lng: selectedPoint.lng },
                },
                radius,
            });
        }
    }, [isOpen, page, radius, selectedPoint, updateResultsPreview]);
    useEffect(() => {
        // When moving from radius to base, we want to reset the state
        // Going back to the radius should be like it was never used.
        if (variant !== 'radius') {
            setPreviousInput('');
            setPage(Page.Geogos);
            setSelectedPoint(undefined);
            setRadius(defaultRadius);
            setPreviousSelection(value);
        }
    }, [value, variant]);
    const initializedInputValue = useMemo(() => {
        if (value.responseFrom === 'geogos') {
            return mapper.translateGeoEntry(value.entry).inputLabel;
        }
        else {
            return value.entry.friendlyName;
        }
    }, [value.entry, value.responseFrom]);
    const { getComboboxProps, getInputProps, getItemProps, getMenuProps, highlightedIndex, inputValue, setInputValue, selectedItem, } = useCombobox({
        id: 'mobile-geo-search',
        isOpen,
        items: options.items,
        labelId,
        initialInputValue: initializedInputValue,
        itemToString: (item) => item?.inputLabel ?? '',
        onInputValueChange: (changes) => {
            const service = page === Page.RadiusMap ? 'here' : 'geogos';
            if (changes.type === useCombobox.stateChangeTypes.ItemClick) {
                // If an item has just been selected, do not do a Geogos search
                updateItems(service, '');
            }
            // Block request when cancelling search
            // NB: Retyping the current query will not search the final letter
            else if (isOpen &&
                changes.inputValue !== '' &&
                previousInput !== changes.inputValue) {
                updateItems(service, changes.inputValue);
            }
        },
        onSelectedItemChange: ({ selectedItem }) => {
            if (selectedItem == null) {
                return;
            }
            textFieldRef.current?.blur();
            if (isGeogosAutocompleteOption(selectedItem)) {
                pulse?.queueEvent(buildSearchSubmitEvent(selectedItem.value, selectedItem.inputLabel, highlightedIndex + 1, selectedItem.isRecentSearch));
                setPreviousSelection(selectedItem.value);
                onSelect(selectedItem.value);
                setIsOpen(false);
                setSelectedPoint(undefined);
                setRadius(defaultRadius);
                storeRecentSearch?.(selectedItem);
            }
            else {
                const { lat, lng } = selectedItem.value.entry.point;
                setSelectedPoint({
                    lat,
                    lng,
                    friendlyName: selectedItem.value.entry.friendlyName,
                    addressToTrack: selectedItem.value.entry.addressToTrack,
                });
            }
        },
        onStateChange: (changes) => {
            // Only intervene if the selected item hasn't changed.
            // Otherwise let Downshift do the work.
            if (changes.type === useCombobox.stateChangeTypes.ItemClick &&
                changes.selectedItem == null) {
                textFieldRef.current?.blur();
                setIsOpen(false);
                pulse?.queueEvent(buildSearchSubmitEvent(selectedItem?.value, selectedItem?.inputLabel, highlightedIndex + 1, selectedItem?.isRecentSearch));
            }
        },
    });
    useEffect(() => {
        if (!isOpen) {
            if (value.responseFrom === 'here') {
                setInputValue(value.entry.friendlyName);
                if (value.radius > 0) {
                    setRadius(value.radius);
                }
                else {
                    console.error(`Invalid radius ${value.radius}m, using the default value instead.`);
                    setRadius(defaultRadius);
                }
                setPreviousInput('');
                setPage(Page.RadiusMap);
                const point = {
                    friendlyName: value.entry.friendlyName,
                    lat: value.entry.point.lat,
                    lng: value.entry.point.lng,
                };
                setSelectedPoint(point);
                setRadius(value.radius);
                setPreviousSelection(value);
            }
            else {
                const nameToDisplay = mapper.translateGeoEntry(value.entry, value.searchNearRegions).inputLabel;
                setInputValue(nameToDisplay);
                setPreviousInput('');
                setPage(Page.Geogos);
                setSelectedPoint(undefined);
                setRadius(defaultRadius);
                setPreviousSelection(value);
            }
        }
    }, [value, setInputValue, setRadius, isOpen]);
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add(classes['no-scroll']);
            document.documentElement.classList.add(classes['full-height']);
            textFieldRef.current?.scrollIntoView?.(true);
        }
        else {
            document.body.classList.remove(classes['no-scroll']);
            document.documentElement.classList.remove(classes['full-height']);
        }
    }, [isOpen]);
    const cancelSearch = useCallback(() => {
        if (page === Page.Geogos) {
            pulse?.queueEvent(buildSearchCancelEvent());
            updateItems('geogos', '');
        }
        else {
            pulse?.queueEvent(buildRadiusSearchCancelEvent());
            // Empty options.HereItems
            updateItems('here', '');
        }
        onSelect(previousSelection);
        setIsOpen(false);
        textFieldRef.current?.blur();
    }, [page, updateItems, pulse, previousSelection, onSelect]);
    const clearInput = useCallback(() => {
        setInputValue('');
        textFieldRef.current?.focus();
    }, [setInputValue]);
    const pointSelected = selectedPoint != null;
    return (
    // the onClick is only there to stop event bubbling
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    React.createElement("div", { className: classNames(classes.dialog, { [classes.fullscreen]: isOpen }), onClick: (e) => {
            e.stopPropagation();
        } },
        React.createElement("div", { ...getComboboxProps(), className: classes.dialogHeader },
            React.createElement("button", { type: "button", className: classes.back, onClick: cancelSearch, "aria-label": "Torna indietro" },
                React.createElement("img", { src: `${assetsBase}/static/icons/cactus/back-arrow.svg`, width: "24", height: "24", alt: "", crossOrigin: "anonymous" })),
            React.createElement(TextField, { size: "large", icon: "Pin", withClearIcon: isOpen && inputValue?.length > 0, onInputChange: (e) => setInputValue(e.target.value), onCleared: clearInput, disableNativeAC: true, ...getInputProps({
                    ref: textFieldRef,
                    type: 'search',
                    placeholder: page === Page.RadiusMap
                        ? 'Indirizzo, quartiere, città, o CAP'
                        : 'Comune, Provincia o Regione',
                    name,
                    value: inputValue,
                    onFocus: () => {
                        if (!isOpen) {
                            setIsOpen(true);
                            setPage(Page.Geogos);
                            setPreviousInput(inputValue || '');
                            setInputValue('');
                            setTimeout(() => {
                                window.scrollTo(0, 0);
                                document.body.scrollTop = 0;
                            }, 250);
                        }
                    },
                }) }),
            !isOpen && variant === 'radius' ? (React.createElement(Button, { design: "solid-lite", iconRight: pointSelected ? (React.createElement(Icon, { name: "ArrowDown", iconSize: IconSize.SM, fillColor: "currentColor" })) : undefined, onClick: () => {
                    setPreviousInput(inputValue);
                    if (page !== Page.RadiusMap) {
                        setPage(Page.RadiusMap);
                        setInputValue('');
                    }
                    else {
                        textFieldRef.current?.blur();
                    }
                    setIsOpen(true);
                    if (selectedPoint == null) {
                        // SetTimeout so that the focus does not open the Geogos Search
                        setTimeout(() => textFieldRef.current?.focus(), 0);
                    }
                    pulse?.queueEvent(buildRadiusSearchInitiateEvent({
                        label: radius != null
                            ? formatDistance(radius)
                            : 'Ricerca con raggio',
                    }));
                }, classes: [classes['radius-search-trigger']] }, pointSelected ? formatDistance(radius) : 'Cerca per distanza')) : null),
        React.createElement("ul", { ...getMenuProps(), className: classNames(classes.menu, {
                [classes['map-menu']]: page === Page.RadiusMap && isOpen,
                [classes['map-menu-empty']]: page === Page.RadiusMap &&
                    isOpen &&
                    (options.items.length === 0 || options.kind !== 'map'),
            }) },
            isOpen &&
                ((page === Page.Geogos && options.kind === 'search') ||
                    (page === Page.RadiusMap && options.kind === 'map')) &&
                options.items.map((item, index) => (React.createElement("li", { className: classNames(itemClasses.item, {
                        [itemClasses.selected]: highlightedIndex === index,
                    }), key: `${item.inputLabel}${index}`, ...getItemProps({ item, index }) },
                    React.createElement(SuggestionResult, { geoOptionsLabels: item.optionLabels, query: page === Page.Geogos ? inputValue : undefined }),
                    ' '))),
            isOpen && page === Page.Geogos && options.kind === 'recent' && (React.createElement(RecentSearches, { getItemProps: getItemProps, highlightedIndex: highlightedIndex, searches: options.items, selectedItem: selectedItem == null || isGeogosAutocompleteOption(selectedItem)
                    ? selectedItem
                    : null, assetsBase: assetsBase, variant: "mobile" })),
            isOpen && page === Page.Geogos && options.kind === 'new' && (React.createElement(NewSearch, { assetsBase: assetsBase }))),
        isOpen && page === Page.RadiusMap ? (React.createElement("div", { id: classes['radius-actions'], className: "theme-purpleImmobili" },
            selectedPoint == null ? (React.createElement("div", { id: classes['helper-icon'] },
                React.createElement(Map, { className: classNames(classes['radius-menu-chunk']) }),
                React.createElement("h3", { className: classNames('headline-6', classes['radius-menu-chunk'], classes['radius-menu-text']) }, "Cerca per distanza"),
                React.createElement("p", { className: classNames('body-text', classes['radius-menu-chunk'], classes['radius-menu-text']) }, "Indica un luogo e seleziona poi un raggio di distanza massimo."))) : null,
            selectedPoint != null ? (React.createElement(React.Fragment, null,
                React.createElement(HereMap, { apiKey: hereApiKey, selectedPoint: selectedPoint, radius: radius, assetsBase: assetsBase }),
                React.createElement("div", { className: classes['confirmation'] },
                    React.createElement(RadiusPicker, { startingRadius: radius, setRadius: setRadius }),
                    React.createElement(Button, { size: "large", classes: [classes.apply, classes['radius-menu-chunk']], onClick: () => {
                            const hereSelection = {
                                responseFrom: 'here',
                                radius,
                                entry: {
                                    point: {
                                        lat: selectedPoint.lat,
                                        lng: selectedPoint.lng,
                                    },
                                    friendlyName: selectedPoint.friendlyName,
                                    addressToTrack: selectedPoint.addressToTrack,
                                },
                            };
                            onSelect(hereSelection);
                            setInputValue(selectedPoint.friendlyName);
                            setPreviousSelection(hereSelection);
                            setIsOpen(false);
                        } }, resultsPreview != undefined
                        ? `Vedi ${Intl.NumberFormat('it').format(resultsPreview)} risultat${resultsPreview === 1 ? 'o' : 'i'}`
                        : 'Vedi i risultati')))) : null)) : null));
}
const NewSearch = ({ assetsBase }) => (React.createElement("div", { className: classes.fallback },
    React.createElement("h6", { className: "headline-6" }, "Inserisci il luogo"),
    React.createElement("p", { className: classNames(classes.helptext, 'body-text small') }, "Puoi cercare per comune, provincia o regione scrivendo nella barra di ricerca in alto"),
    React.createElement("img", { className: classes.geomap, src: `${assetsBase}/static/illustrations/GeolocationMap.png`, alt: "", width: "186", height: "140", crossOrigin: "anonymous" })));
function RadiusPicker({ startingRadius = defaultRadius, setRadius, }) {
    const onRadiusChange = React.useCallback((newRadius) => {
        setRadius(newRadius);
    }, [setRadius]);
    useEffect(() => {
        setRadius(startingRadius);
    }, [setRadius, startingRadius]);
    return (React.createElement("div", { className: classes['radius-picker'] },
        React.createElement("label", { htmlFor: "radius", className: classNames('body-text semibold', classes['radius-picker-label-text']) }, "Seleziona il raggio"),
        React.createElement(Slider, { min: 500, max: 10000, defaultValue: startingRadius, step: 500, onChangeValue: onRadiusChange, labelBuilder: formatDistance, classes: [classes['radius-slider']] })));
}

export { MobileGeoSearch as default };
//# sourceMappingURL=index.js.map
